import type {
    AiAgentFormId,
    AiAgentIntent,
    AiAgentUiActionPayload,
} from '../../../redux/aiAgent/aiAgent.type';
import store from '../../../store';

/**
 * Client mirror of server SYSTEM_SCHEMA.intentMeta — only what the UI needs.
 * Add a new action here instead of hardcoding it.
 */
type AgentIntentMeta = {
    formId: AiAgentFormId;
    requiresEventTypes: boolean;
    /** Mirrors server intentMeta.requiresEvent — account scope rejects it. */
    requiresEvent: boolean;
    /** aiAgent key naming what this intent creates — the confirm action says it. */
    confirmLabelKey: string;
};

export const AGENT_INTENT_META: Record<AiAgentIntent, AgentIntentMeta> = {
    CREATE_EVENT: {
        formId: 'NEW_EVENT',
        requiresEventTypes: true,
        requiresEvent: false,
        confirmLabelKey: 'proposalConfirmCreateEvent',
    },
    CREATE_TICKET: {
        formId: 'CREATE_TICKET',
        requiresEventTypes: false,
        requiresEvent: true,
        confirmLabelKey: 'proposalConfirmCreateTicket',
    },
};

/** Generic wording for an intent the client does not know yet. */
const FALLBACK_CONFIRM_LABEL_KEY = 'proposalConfirm';

export const getIntentConfirmLabelKey = (intent: AiAgentIntent): string => {
    const meta = AGENT_INTENT_META[intent] as AgentIntentMeta | undefined;
    return meta?.confirmLabelKey ?? FALLBACK_CONFIRM_LABEL_KEY;
};

/**
 * Whether the server would accept this intent in the given scope (mirrors its
 * scopes.allowedIntents) — so the UI never offers an action that is bound to be refused.
 */
export const isIntentAvailableInScope = (
    intent: AiAgentIntent,
    scope: 'account' | 'event',
): boolean => scope === 'event' || !AGENT_INTENT_META[intent].requiresEvent;

export const getIntentForFormId = (formId: string | null | undefined): AiAgentIntent | null => {
    if(!formId) {
        return null;
    }
    const entry = Object.entries(AGENT_INTENT_META).find(([, meta]) => meta.formId === formId);
    return (entry?.[0] as AiAgentIntent) ?? null;
};

/** The intent whose form picks an event type — the one that creates an event. */
export const getEventTypeIntent = (): AiAgentIntent | null => {
    const entry = Object.entries(AGENT_INTENT_META).find(([, meta]) => meta.requiresEventTypes);
    return (entry?.[0] as AiAgentIntent) ?? null;
};

/** Locked chat intent, or intent of the last proposal's form. */
export const getEffectiveSessionIntent = (): AiAgentIntent | null => {
    const {aiAgent} = store.getState();
    return aiAgent?.sessionIntent ?? getIntentForFormId(aiAgent?.activeFormId);
};

/**
 * Which intent a uiAction belongs to. The server already sanitizes payload.intent/formId
 * against the allowed intents. eventType alone implies create-event.
 */
export const getPayloadIntent = (payload: AiAgentUiActionPayload = {}): AiAgentIntent | null => {
    if(payload.intent && payload.intent in AGENT_INTENT_META) {
        return payload.intent;
    }
    const fromFormId = getIntentForFormId(payload.formId);
    if(fromFormId) {
        return fromFormId;
    }
    return payload.eventType ? getEventTypeIntent() : null;
};
